/* eslint-disable no-nested-ternary */
import { createSlice } from '@reduxjs/toolkit';
import { gmbGetReviewStats, gmbListReviews, gmbListAccounts, gmbListLocations, gmbListPosts } from 'src/services/googleMyBusiness';
import { hasFeatureAccess } from 'src/utils/featurePermission';
import { dispatch, store } from '../store';
import { setSelectedGoogleAccount } from './user';

const initialState = {
    // Cache keyed by `${practiceId}_${accountId}_${locationId}`
    reviewStats: {},
    reviews: {},
    needsResponse: {},
    posts: {},
    // Loading state for background syncs
    syncing: false,
    // Last sync timestamp per location
    lastSyncTime: {},
};

// Helper to generate cache key
const getCacheKey = (practiceId, accountId, locationId) =>
    `${practiceId}_${accountId}_${locationId}`;

export const slice = createSlice({
    name: 'reviewStatsCache',
    initialState,
    reducers: {
        setReviewStatsCache(state, action) {
            const { key, data } = action.payload;
            state.reviewStats[key] = data;
            state.lastSyncTime[key] = Date.now();
        },
        setReviewsCache(state, action) {
            const { key, data } = action.payload;
            state.reviews[key] = data;
        },
        setNeedsResponseCache(state, action) {
            const { key, data } = action.payload;
            state.needsResponse[key] = data;
        },
        setPostsCache(state, action) {
            const { key, data } = action.payload;
            state.posts[key] = data;
        },
        setSyncing(state, action) {
            state.syncing = action.payload;
        },
        clearCache(state) {
            state.reviewStats = {};
            state.reviews = {};
            state.needsResponse = {};
            state.posts = {};
            state.lastSyncTime = {};
        },
        removeLocationCache(state, action) {
            const { key } = action.payload;
            delete state.reviewStats[key];
            delete state.reviews[key];
            delete state.needsResponse[key];
            delete state.posts[key];
            delete state.lastSyncTime[key];
        },
    },
});

export const {
    setReviewStatsCache,
    setReviewsCache,
    setNeedsResponseCache,
    setPostsCache,
    setSyncing,
    clearCache,
    removeLocationCache
} = slice.actions;

export default slice.reducer;

// Thunk to prefetch all Google reviews data for a location
export function prefetchGoogleReviewsData(practiceId, accountId, locationId) {
    return async () => {
        if (!practiceId || !accountId || !locationId) return;
        if (!hasFeatureAccess('google-reviews')) return;

        const key = getCacheKey(practiceId, accountId, locationId);

        try {
            // Fetch all data in parallel
            const [reviewStatsData, reviewsData, needsResponseData] = await Promise.all([
                gmbGetReviewStats(practiceId, accountId, locationId).catch(() => null),
                gmbListReviews(practiceId, accountId, locationId, { limit: 25, offset: 0 }).catch(() => null),
                gmbListReviews(practiceId, accountId, locationId, { needs_reply: true, limit: 100 }).catch(() => null),
            ]);

            if (reviewStatsData) {
                dispatch(setReviewStatsCache({ key, data: reviewStatsData }));
            }
            if (reviewsData) {
                dispatch(setReviewsCache({ key, data: reviewsData }));
            }
            if (needsResponseData) {
                const results = Array.isArray(needsResponseData?.results)
                    ? needsResponseData.results
                    : Array.isArray(needsResponseData) ? needsResponseData : [];
                dispatch(setNeedsResponseCache({ key, data: results }));
            }
        } catch (err) {
            // Silently fail for prefetch - don't disrupt user experience
            console.warn('Prefetch Google reviews data failed:', err);
        }
    };
}

// Keep old function name for backward compatibility
export const prefetchReviewStats = prefetchGoogleReviewsData;

// Comprehensive prefetch that loads accounts, locations, and all review data
// This mimics what happens when opening the Google Reviews page
export function prefetchGoogleReviewsOnDashboardLoad(practiceId) {
    return async () => {
        if (!practiceId) return;
        if (!hasFeatureAccess('google-reviews')) return;

        try {
            // Get saved account from Redux
            const state = store.getState();
            const savedGoogleAccount = state.user?.selectedGoogleAccount;

            // 1. Load all accounts
            const accountsResponse = await gmbListAccounts(practiceId);
            const accounts = Array.isArray(accountsResponse?.results)
                ? accountsResponse.results
                : Array.isArray(accountsResponse) ? accountsResponse : [];

            if (accounts.length === 0) {
                return; // No accounts to prefetch
            }

            // 2. Load locations for all accounts in parallel
            const locationPromises = accounts.map(async (account) => {
                try {
                    const locResponse = await gmbListLocations(practiceId, account.id);
                    const locations = Array.isArray(locResponse?.results)
                        ? locResponse.results
                        : Array.isArray(locResponse) ? locResponse : [];
                    // Filter connected locations
                    const connectedLocations = locations.filter((l) => l?.is_connected !== false);
                    return { account, locations: connectedLocations };
                } catch {
                    return { account, locations: [] };
                }
            });

            const accountsWithLocations = await Promise.all(locationPromises);

            // Flatten to get all location entries
            const allLocations = accountsWithLocations.flatMap(({ account, locations }) =>
                locations.map((location) => ({ account, location }))
            );

            if (allLocations.length === 0) {
                return; // No locations to prefetch
            }

            // 3. Determine which account/location to use
            let accountToUse = null;
            let locationToUse = null;

            if (savedGoogleAccount?.accountId) {
                // Try to find saved account/location
                const savedEntry = allLocations.find(
                    (entry) => entry.account?.id === savedGoogleAccount.accountId
                );
                if (savedEntry) {
                    accountToUse = savedEntry.account;
                    // Find saved location or use first one for this account
                    const accountLocations = allLocations.filter(
                        (x) => x.account?.id === savedEntry.account.id
                    );
                    if (savedGoogleAccount.locationId) {
                        // Match by database id (savedGoogleAccount.locationId stores the DB id)
                        const savedLocation = accountLocations.find(
                            (x) => x.location?.id === savedGoogleAccount.locationId
                        );
                        locationToUse = savedLocation?.location || accountLocations[0]?.location || null;
                    } else {
                        locationToUse = accountLocations[0]?.location || null;
                    }
                }
            }

            // If no saved account found, use first available
            if (!accountToUse && allLocations.length > 0) {
                accountToUse = allLocations[0].account;
                locationToUse = allLocations[0].location;
            }

            if (!accountToUse || !locationToUse) {
                return; // No valid account/location to prefetch
            }

            // Use database `id` for API calls (not location_id which is Google's identifier)
            const locationDbId = locationToUse.id;

            // 4. Save selected account to Redux if not already saved
            if (!savedGoogleAccount?.accountId || savedGoogleAccount.accountId !== accountToUse.id) {
                dispatch(setSelectedGoogleAccount({
                    accountId: accountToUse.id,
                    locationId: locationDbId,
                }));
            }

            // 5. Prefetch all review data and posts for this location
            const key = getCacheKey(practiceId, accountToUse.id, locationDbId);

            const [reviewStatsData, reviewsData, needsResponseFirstPage, postsData] = await Promise.all([
                gmbGetReviewStats(practiceId, accountToUse.id, locationDbId).catch(() => null),
                gmbListReviews(practiceId, accountToUse.id, locationDbId, { limit: 25, offset: 0 }).catch(() => null),
                gmbListReviews(practiceId, accountToUse.id, locationDbId, { reply__isnull: 'true', limit: 50, offset: 0, ordering: '-create_time' }).catch(() => null),
                gmbListPosts(practiceId, accountToUse.id, locationDbId, { ordering: '-create_time' }).catch(() => null),
            ]);

            if (reviewStatsData) {
                dispatch(setReviewStatsCache({ key, data: reviewStatsData }));
            }
            if (reviewsData) {
                dispatch(setReviewsCache({ key, data: reviewsData }));
            }
            if (needsResponseFirstPage) {
                const firstBatch = Array.isArray(needsResponseFirstPage?.results)
                    ? needsResponseFirstPage.results
                    : Array.isArray(needsResponseFirstPage) ? needsResponseFirstPage : [];
                const totalCount = typeof needsResponseFirstPage?.count === 'number' ? needsResponseFirstPage.count : firstBatch.length;

                let allResults = [...firstBatch];

                // If there are more records, fetch remaining pages in parallel (API max is 50)
                if (totalCount > 50 && firstBatch.length === 50) {
                    const remainingPages = Math.ceil((totalCount - 50) / 50);
                    const pagePromises = Array.from({ length: Math.min(remainingPages, 19) }, (_, i) => {
                        const offset = (i + 1) * 50;
                        return gmbListReviews(practiceId, accountToUse.id, locationDbId, {
                            limit: 50,
                            offset,
                            reply__isnull: 'true',
                            ordering: '-create_time',
                        }).catch(() => ({ results: [] }));
                    });

                    const pageResponses = await Promise.all(pagePromises);
                    pageResponses.forEach((resp) => {
                        const batch = Array.isArray(resp?.results) ? resp.results : Array.isArray(resp) ? resp : [];
                        allResults = [...allResults, ...batch];
                    });
                }

                dispatch(setNeedsResponseCache({ key, data: { data: allResults, count: totalCount } }));
            }
            if (postsData) {
                const postsArr = Array.isArray(postsData?.results)
                    ? postsData.results
                    : Array.isArray(postsData) ? postsData : [];
                dispatch(setPostsCache({ key, data: postsArr }));
            }

        } catch (err) {
            // Silently fail for prefetch - don't disrupt user experience
            console.warn('Prefetch Google reviews on dashboard load failed:', err);
        }
    };
}

// Thunk to sync review stats in background (with syncing indicator)
export function syncReviewStatsInBackground(practiceId, accountId, locationId, onComplete) {
    return async () => {
        if (!practiceId || !accountId || !locationId) return;
        if (!hasFeatureAccess('google-reviews')) return;

        const key = getCacheKey(practiceId, accountId, locationId);

        dispatch(setSyncing(true));

        try {
            const data = await gmbGetReviewStats(practiceId, accountId, locationId);
            dispatch(setReviewStatsCache({ key, data }));
            if (onComplete) onComplete(true, data);
        } catch (err) {
            console.warn('Background sync review stats failed:', err);
            if (onComplete) onComplete(false, null);
        } finally {
            dispatch(setSyncing(false));
        }
    };
}

// Selector to get cached review stats for a location
export const selectCachedReviewStats = (state, practiceId, accountId, locationId) => {
    if (!practiceId || !accountId || !locationId) return null;
    const key = getCacheKey(practiceId, accountId, locationId);
    return state.reviewStatsCache?.reviewStats?.[key] || null;
};

// Selector to get cached reviews for a location
export const selectCachedReviews = (state, practiceId, accountId, locationId) => {
    if (!practiceId || !accountId || !locationId) return null;
    const key = getCacheKey(practiceId, accountId, locationId);
    return state.reviewStatsCache?.reviews?.[key] || null;
};

// Selector to get cached needs response for a location
export const selectCachedNeedsResponse = (state, practiceId, accountId, locationId) => {
    if (!practiceId || !accountId || !locationId) return null;
    const key = getCacheKey(practiceId, accountId, locationId);
    return state.reviewStatsCache?.needsResponse?.[key] || null;
};

// Selector to get cached posts for a location
export const selectCachedPosts = (state, practiceId, accountId, locationId) => {
    if (!practiceId || !accountId || !locationId) return null;
    const key = getCacheKey(practiceId, accountId, locationId);
    return state.reviewStatsCache?.posts?.[key] || null;
};

// Selector to check if we have cached data
export const selectHasCachedData = (state, practiceId, accountId, locationId) => {
    if (!practiceId || !accountId || !locationId) return false;
    const key = getCacheKey(practiceId, accountId, locationId);
    return !!(
        state.reviewStatsCache?.reviewStats?.[key] ||
        state.reviewStatsCache?.reviews?.[key] ||
        state.reviewStatsCache?.needsResponse?.[key]
    );
};

// Selector to get syncing state
export const selectIsSyncing = (state) => state.reviewStatsCache?.syncing || false;

// Selector to get last sync time
export const selectLastSyncTime = (state, practiceId, accountId, locationId) => {
    if (!practiceId || !accountId || !locationId) return null;
    const key = getCacheKey(practiceId, accountId, locationId);
    return state.reviewStatsCache?.lastSyncTime?.[key] || null;
};

// Helper to get cache key (exported for use in components)
export { getCacheKey };
